import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Ellipsis } from '@openx/components-core/lib/Ellipsis/Ellipsis';
import { StatusIndicator } from '@openx/components-core/lib/StatusIndicator/StatusIndicator';
import { ValuePlaceholder } from '@openx/components-core/lib/Value/ValuePlaceholder/ValuePlaceholder';
import type { ListSegment } from '@openx/types/segments';
import type { Columns } from '@openx/types/tableHelpers';
import type { I18nTranslate } from '@openx/types/translations';
import { abbreviatedNumber } from '@openx/utils/lib/abbreviatedNumber';
import { convertToPercent } from '@openx/utils/lib/convertToPercent';
import { ReachTooltip } from '../components/ReachTooltip/ReachTooltip';
import { mapStatus } from '../utils/mapStatus';
import { DateCell } from './parts/DateCell';
import { HighlightedCell } from './parts/HighlightedCell';
import { LabeledValue } from './parts/LabeledValue';
import { NameCell } from './parts/NameCell';

export const getListTableColumns = (t: I18nTranslate, phrase = ''): Columns<ListSegment> => {
  return [
    {
      key: 'name',
      render: ({ name, expiration_date, status }) => {
        if (!name) return null;

        return <NameCell name={name} estimatedExpiryDate={expiration_date} status={status} searchPhrase={phrase} />;
      },
      title: t('Name'),
      width: '11%',
      disabled: true,
    },
    {
      key: 'status',
      render: ({ status: itemStatus }) => {
        const status = mapStatus(itemStatus);

        if (!status) {
          return null;
        }

        return <StatusIndicator label={status.label} status={status.status} />;
      },
      title: t('Status'),
      width: '6%',
    },
    {
      key: 'reach',
      render: ({ id, reach }) =>
        reach ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography component="span">{abbreviatedNumber(reach)}</Typography>
            <ReachTooltip id={id} reach={reach} />
          </Box>
        ) : null,
      title: t('Reach'),
      width: '6%',
    },
    {
      key: 'regions',
      render: ({ regions }) => {
        const text =
          regions
            ?.map(({ region_code }) => region_code)
            .filter(Boolean)
            .join(', ') ?? '';

        return text ? (
          <Ellipsis tooltip tooltipMessage={text} width="100%">
            <HighlightedCell value={text} phrase={phrase} />
          </Ellipsis>
        ) : (
          <ValuePlaceholder />
        );
      },
      title: t('Region'),
      width: '7%',
      hide: true,
    },
    {
      key: 'countries',
      render: ({ countries }) => {
        const text =
          countries
            ?.map(({ country_code }) => country_code)
            .filter(Boolean)
            .join(', ') ?? '';

        return text ? (
          <Ellipsis tooltip tooltipMessage={text} width="100%">
            <HighlightedCell value={text} phrase={phrase} />
          </Ellipsis>
        ) : (
          <ValuePlaceholder />
        );
      },
      title: t('Country'),
      width: '7%',
      hide: true,
    },
    {
      key: 'segment_type',
      render: ({ segment_type }) => (
        <Ellipsis tooltip tooltipMessage={segment_type} width="100%">
          <HighlightedCell value={segment_type} phrase={phrase} />
        </Ellipsis>
      ),
      title: t('Type'),
      width: '9%',
      hide: true,
    },
    {
      key: 'segment_category',
      render: ({ segment_category }) => (
        <Ellipsis tooltip tooltipMessage={segment_category} width="100%">
          <HighlightedCell value={segment_category} phrase={phrase} />
        </Ellipsis>
      ),
      title: t('Category'),
      width: '9%',
    },
    {
      key: 'segment_sub_category',
      render: ({ segment_sub_category }) => (
        <Ellipsis tooltip tooltipMessage={segment_sub_category} width="100%">
          <HighlightedCell value={segment_sub_category} phrase={phrase} />
        </Ellipsis>
      ),
      title: t('Sub Category'),
      width: '9%',
      hide: true,
    },
    {
      key: 'description',
      render: ({ description }) => (
        <Ellipsis tooltip tooltipMessage={description} width="100%">
          <HighlightedCell value={description} phrase={phrase} />
        </Ellipsis>
      ),
      title: t('Description'),
      width: '12%',
    },
    {
      key: 'price',
      render: ({ price_cpm, price_pom, sellable }) =>
        sellable ? (
          <Stack>
            <LabeledValue label="Percent of Media" value={convertToPercent(price_pom ?? 0)} suffix="%" />
            <LabeledValue label="Fixed CPM" value={price_cpm} suffix="$" />
          </Stack>
        ) : (
          <ValuePlaceholder />
        ),
      title: t('Price'),
      width: '10%',
      minWidth: '150px',
    },
    {
      key: 'provider',
      render: ({ provider }) => (
        <Ellipsis tooltip tooltipMessage={provider?.name} width="100%">
          <HighlightedCell value={provider?.name} phrase={phrase} />
        </Ellipsis>
      ),
      title: t('Data Provider'),
      width: '9%',
      hide: true,
    },
    {
      key: 'account',
      render: ({ account }) => (
        <Ellipsis tooltip tooltipMessage={account.name} width="100%">
          <HighlightedCell value={account?.name} phrase={phrase} />
        </Ellipsis>
      ),
      title: t('Data Partner'),
      width: '10%',
    },
    {
      key: 'updated_date',
      render: ({ updated_date }) => <DateCell date={updated_date} />,
      title: t('Last Modified'),
      width: '7%',
      hide: true,
      minWidth: '140px',
    },
  ];
};
